import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { trackAdClickEvent, trackLeadEvent } from "@/lib/leadTracking";
import { ASO_API, buildAsoAuditPayload, buildSampleAsoReport } from "@/lib/asoEngine";
import { AsoHeroForm } from "@/components/equinox/aso-engine/AsoHeroForm";
import { AsoLeadCaptureGate } from "@/components/equinox/aso-engine/AsoLeadCaptureGate";
import { AsoLoadingExperience } from "@/components/equinox/aso-engine/AsoLoadingExperience";
import { AsoMethodologyDialog } from "@/components/equinox/aso-engine/AsoMethodologyDialog";
import { AsoReportDashboard } from "@/components/equinox/aso-engine/AsoReportDashboard";
import { AsoVideoBriefing } from "@/components/equinox/aso-engine/AsoVideoBriefing";

const statusFallback = [
  { label: "Fetching app metadata", delay_ms: 1500 },
  { label: "Analyzing ratings and reviews", delay_ms: 1500 },
  { label: "Extracting ASO keywords", delay_ms: 1500 },
  { label: "Benchmarking competitors", delay_ms: 1500 },
  { label: "Evaluating screenshots and creatives", delay_ms: 1500 },
  { label: "Measuring sentiment trends", delay_ms: 1500 },
  { label: "Calculating conversion potential", delay_ms: 1500 },
  { label: "Running Equinox AI Engine", delay_ms: 1500 },
  { label: "Generating personalized recommendations", delay_ms: 1500 },
  { label: "Preparing founder video briefing", delay_ms: 1500 },
];

export default function AsoAiEnginePage() {
  const reportRef = useRef(null);
  const [form, setForm] = useState({ founder_name: "", work_email: "", company_name: "", app_url: "" });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeStatus, setActiveStatus] = useState(statusFallback[0].label);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    if (!loading) return undefined;
    const statuses = report?.progress_stages || statusFallback;
    let statusIndex = 0;
    let currentProgress = 0;
    const interval = window.setInterval(() => {
      currentProgress += 1.8;
      setProgress(Math.min(100, currentProgress));
    }, 280);

    const statusTimer = window.setInterval(() => {
      statusIndex = Math.min(statusIndex + 1, statuses.length - 1);
      setActiveStatus(statuses[statusIndex].label);
    }, 1600);

    const doneTimer = window.setTimeout(() => {
      setProgress(100);
      setLoading(false);
      setShowVideo(true);
      trackLeadEvent("cta_click", { label: "aso_report_ready", metadata: { source: "aso_ai_engine" } });
    }, 16000);

    return () => {
      window.clearInterval(interval);
      window.clearInterval(statusTimer);
      window.clearTimeout(doneTimer);
    };
  }, [loading, report]);

  const runAudit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      setProgress(0);
      setActiveStatus(statusFallback[0].label);
      trackAdClickEvent({ label: "aso_generate_report", metadata: { placement: "aso_hero_form" } });
      const response = await axios.post(`${ASO_API}/start`, buildAsoAuditPayload(form));
      setReport(response.data);
      setShowVideo(false);
      toast.success("Equinox AI Engine is generating your report.");
    } catch (error) {
      setLoading(false);
      toast.error(error?.response?.data?.detail || "Unable to start the ASO audit right now.");
    }
  };

  const watchSample = () => {
    setReport(buildSampleAsoReport());
    setShowVideo(true);
    trackAdClickEvent({ label: "aso_watch_sample", metadata: { placement: "aso_hero_form" } });
  };

  return (
    <>
      <AsoHeroForm
        form={form}
        onChange={(key, value) => setForm((current) => ({ ...current, [key]: value }))}
        onSubmit={runAudit}
        onWatchSample={watchSample}
        submitting={loading}
      />

      {loading ? <AsoLoadingExperience progress={progress} activeStatus={activeStatus} /> : null}
      {showVideo && report ? <AsoVideoBriefing report={report} onComplete={() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} /> : null}
      {report ? <AsoReportDashboard report={report} reportRef={reportRef} methodologyTrigger={<AsoMethodologyDialog />} /> : null}
      {report ? <AsoLeadCaptureGate report={report} onQualified={setReport} /> : null}
    </>
  );
};